import { authServiceApi } from "../../../../app/authServiceApi";
import { API_URL } from "../../../../constants/apiUrl";
import { CommonResponse, GetQueryArgsType } from "../../../../types";
import {
  AllContractParams,
  AllContractResponse,
  AllKPIAchievementParams,
  AllKPIAchievementResponse,
  AllKpiPerformanceParams,
  AllKpiPerformanceResponse,
  AllSHiftHistoryResponse,
  AllShiftHistoryParams,
  AllSlabHistoryParams,
  AllSlabHistoryResponse,
} from "./jobDetails.type";

export const jobDetails = authServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    // ------ Mutations ------
    assignShift: builder.mutation({
      query: (data) => ({
        url: API_URL.assignShift,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return [{ type: "GetJobDetails", id: "shiftHistory" }];
        return [];
      },
    }),

    assignSalarySlab: builder.mutation({
      query: (data) => ({
        url: API_URL.assignSalarySlab,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return [{ type: "GetJobDetails", id: "slabHistory" }];
        return [];
      },
    }),

    renewContract: builder.mutation({
      query: (data) => ({
        url: API_URL.renewContract,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return [{ type: "GetJobDetails", id: "contractHistory" }];
        return [];
      },
    }),

    assignKpiGroup: builder.mutation({
      query: ({ data, id }) => ({
        url: `${API_URL.assignKpiGroup}/${id}/assign`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) =>
        result ? [{ type: "GetJobDetails", id: "kpiPerformance" }] : [],
    }),

    // ------ Queries ------
    getAllShiftHistory: builder.query<
      CommonResponse<AllSHiftHistoryResponse>,
      GetQueryArgsType<AllShiftHistoryParams>
    >({
      query: (arg) => ({
        url: `${API_URL.getAllShiftHistory}`,
        ...arg,
      }),
      providesTags: [{ type: "GetJobDetails", id: "shiftHistory" }],
    }),

    getAllSlabHistory: builder.query<
      CommonResponse<AllSlabHistoryResponse>,
      { arg: GetQueryArgsType<AllSlabHistoryParams>; id: number }
    >({
      query: ({ arg, id }) => ({
        url: `${API_URL.getAllSlabHistory}/${id}`,
        ...arg,
      }),
      providesTags: [{ type: "GetJobDetails", id: "slabHistory" }],
    }),

    getAllKPIPerformanceHistory: builder.query<
      CommonResponse<AllKpiPerformanceResponse>,
      {
        arg: GetQueryArgsType<AllKpiPerformanceParams>;
        jobApplyId: number;
        memberId: number;
      }
    >({
      query: ({ arg, jobApplyId, memberId }) => ({
        url: `${API_URL.getAllKpiPerformanceHistory}/${jobApplyId}/member/${memberId}`,
        ...arg,
      }),
      providesTags: [{ type: "GetJobDetails", id: "kpiPerformance" }],
    }),

    getAllContractHistory: builder.query<
      CommonResponse<AllContractResponse>,
      { arg: GetQueryArgsType<AllContractParams>; id: number }
    >({
      query: ({ arg, id }) => ({
        url: `${API_URL.getAllContractHistory}/${id}`,
        ...arg,
      }),
      providesTags: (result, error, { id }) => [
        { type: "GetJobDetails", id: "contractHistory" },
      ],
    }),

    getKPIAchievement: builder.query<
      CommonResponse<AllKPIAchievementResponse>,
      GetQueryArgsType<AllKPIAchievementParams>
    >({
      query: (arg) => ({
        url: `${API_URL.getKPIAchievement}`,
        ...arg,
      }),
      providesTags: () => ["GetKPIAchievement"],
    }),
  }),
});

export const {
  // ------ Mutation Hooks ------
  useAssignShiftMutation,
  useAssignSalarySlabMutation,
  useRenewContractMutation,
  useAssignKpiGroupMutation,

  // ------ Query Hooks ------
  useGetAllShiftHistoryQuery,
  useGetAllSlabHistoryQuery,
  useGetAllKPIPerformanceHistoryQuery,
  useGetAllContractHistoryQuery,
  useGetKPIAchievementQuery,
} = jobDetails;
